/**
 * LiveScorecardModal — full cricinfo-style scorecard inside a modal.
 *
 * Triggered from the BBB scoring page's "Scorecard" button. Fetches the
 * server's authoritative MatchScorecardView (which includes everything
 * up to the last ball persisted) and renders the same sections we ship
 * on the standalone /cricket/matches/<id>/scorecard route:
 *
 *   - Match header (teams, venue, date, toss, result)
 *   - Per innings:
 *       Batting card (R / B / 4s / 6s / SR / NO)
 *       Extras + Boundaries + Total footer
 *       Did Not Bat chips
 *       Fall of wickets chips
 *       Partnerships table (derived from FOW)
 *       Bowling card (O / M / R / W / Econ + totals row)
 */
import { useEffect, useRef, useState } from "react";
import { X, RefreshCcw } from "lucide-react";
import api from "../../api/axios";
import useDialogDismiss from "../../hooks/useDialogDismiss";
import { isHundred, formatInningsProgress, formatBowlerBalls, bowlerBallsColumnLabel } from "../../utils/hundredDisplay";

// 2026-05-29 — convert a raw legal-ball count to overs notation
// ("44" → "7.2"). Used everywhere the scorecard previously surfaced
// a bare ball count (batting balls faced, partnership balls, etc.)
// per founder ask: "instead of balls it should be overs, count that
// as overs like 3.1 4.5 etc". Returns "—" for null / undefined input
// so empty cells stay readable.
function ballsToOvers(b) {
  if (b == null) return "—";
  const n = Number(b);
  if (!Number.isFinite(n) || n < 0) return "—";
  return `${Math.floor(n / 6)}.${n % 6}`;
}

export default function LiveScorecardModal({ open, matchId, onClose, refreshKey }) {
  const panelRef = useRef(null);
  useDialogDismiss({ open, onClose, ref: panelRef });
  const [data, setData] = useState(null);
  // Server-computed partnerships keyed by innings_number. Fetched from
  // /api/matches/.../partnerships/ in parallel with the scorecard so the
  // modal renders in one paint. Failure is non-fatal — InningsBlock falls
  // back to FOW-derived partnerships when the map is empty.
  const [partnershipsByInnings, setPartnershipsByInnings] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchScorecard = async () => {
    if (!matchId) return;
    setLoading(true);
    setError(null);
    try {
      const [scorecard, partnerships] = await Promise.all([
        api.get(`/api/matches/matches/${matchId}/scorecard/`),
        api.get(`/api/matches/matches/${matchId}/partnerships/`).catch(() => null),
      ]);
      setData(scorecard.data);
      if (partnerships?.data?.innings) {
        const byInn = {};
        for (const innBlock of partnerships.data.innings) {
          byInn[innBlock.innings_number] = innBlock.partnerships || [];
        }
        setPartnershipsByInnings(byInn);
      } else {
        setPartnershipsByInnings({});
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load scorecard");
    } finally {
      setLoading(false);
    }
  };

  // 2026-07-08 — added refreshKey so BallByBallPage can auto-refetch
  // the scorecard (and its partnership table) when the aggregate
  // changes while the modal is open. Founder previously had to hit
  // the Refresh button after every ball, and the Partnerships block
  // consistently lagged behind live scoring.
  useEffect(() => {
    if (open) fetchScorecard();
  }, [open, matchId, refreshKey]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-2 sm:p-4"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Live scorecard"
        className="bg-bg-primary rounded-xl sm:rounded-2xl max-w-5xl w-full max-h-[95vh] sm:max-h-[92vh] border border-bg-secondary shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border-default shrink-0">
          <div className="flex items-baseline gap-3 min-w-0">
            <h3 className="text-base font-semibold text-text-primary truncate">
              {data?.team1?.name && data?.team2?.name
                ? `${data.team1.name} vs ${data.team2.name}`
                : "Live scorecard"}
            </h3>
            {data?.match_type?.code && (
              <span className="text-[10px] uppercase tracking-wider text-text-secondary">
                {data.match_type.code}
              </span>
            )}
            {data?.match?.match_date && (
              <span className="text-[10px] text-text-secondary">{data.match.match_date}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchScorecard}
              disabled={loading}
              className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-bg-secondary text-text-secondary hover:text-text-primary disabled:opacity-40 flex items-center gap-1"
              title="Refresh — pulls the latest from server"
            >
              <RefreshCcw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Refreshing…" : "Refresh"}
            </button>
            <button onClick={onClose} className="text-text-secondary hover:text-text-primary p-1">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading && !data && <div className="text-text-secondary text-sm">Loading scorecard…</div>}
          {error && (
            <div className="bg-rose-500/10 border border-rose-500/30 rounded p-3 text-sm text-rose-300">
              {error}
            </div>
          )}
          {data && (
            <>
              <MatchSummary data={data} />
              {(data.innings || []).map((inn) => (
                <InningsBlock
                  key={inn.innings_number}
                  inn={inn}
                  matchType={data.match_type}
                  playingXI={data.playing_xi}
                  partnerships={partnershipsByInnings[inn.innings_number]}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MatchSummary({ data }) {
  const result = resultLine(data);
  return (
    <div className="bg-bg-secondary/40 border border-border-default rounded-lg p-4">
      <div className="flex flex-wrap items-baseline gap-3 text-sm mb-2">
        {data.stadium?.name && (
          <span className="text-text-secondary">{data.stadium.name}{data.stadium.city ? `, ${data.stadium.city}` : ""}</span>
        )}
        {data.competition?.name && (
          <span className="text-text-secondary">· {data.competition.name}{data.competition.season ? ` ${data.competition.season}` : ""}</span>
        )}
      </div>
      {data.toss && (
        <p className="text-xs text-text-secondary">
          <span className="uppercase tracking-wider text-[10px]">Toss · </span>
          <span className="text-text-primary">{data.toss.won_by_name}</span> chose to {data.toss.decision === "BAT" ? "bat" : "bowl"}
        </p>
      )}
      {result && (
        <p className="text-sm text-text-primary mt-2 font-medium">{result}</p>
      )}
    </div>
  );
}

function resultLine(data) {
  const rt = data?.match?.result_type;
  if (rt === "NO_RESULT") return "Result: No result";
  if (rt === "ABANDONED") return "Result: Abandoned";
  if (rt === "TIE")        return "Result: Match tied";
  if (rt === "DRAW" || data?.result?.draw) return "Result: Match drawn";
  if (data?.result?.winner_name && data?.result?.by_runs != null)
    return `${data.result.winner_name} won by ${data.result.by_runs} run${data.result.by_runs === 1 ? "" : "s"}`;
  if (data?.result?.winner_name && data?.result?.by_wickets != null)
    return `${data.result.winner_name} won by ${data.result.by_wickets} wicket${data.result.by_wickets === 1 ? "" : "s"}`;
  if (data?.match?.state === "IN_PROGRESS") return "Match in progress";
  return null;
}

function InningsBlock({ inn, matchType, playingXI, partnerships: serverPartnerships }) {
  const agg = inn.aggregate;
  // Did Not Bat
  // 2026-05-29 — match by player_id (was player_name). After the
  // jersey-prefix change, names could diverge between playing_xi and
  // batting_card if either side regenerated names with stale state.
  // Matching by id is the canonical join and works regardless of how
  // either side formats the display string.
  const battingXI = playingXI?.[inn.batting_team_id] || [];
  const battedIds = new Set((inn.batting_card || []).map((b) => String(b.player_id)));
  const dnb = battingXI.filter((p) => !battedIds.has(String(p.player_id)));
  // Fall of wickets
  const fow = inn.fow || [];
  // Partnerships — server-computed (richer; includes balls + per-batter
  // splits) preferred. Fall back to FOW-derived if the endpoint failed.
  const partnerships = (() => {
    if (Array.isArray(serverPartnerships) && serverPartnerships.length > 0) {
      return serverPartnerships.map((p) => ({
        ...p,
        ended_by: p.ended_by_name || p.ended_by || "",
      }));
    }
    const out = [];
    let prev = 0;
    for (const row of fow) {
      if (!Array.isArray(row) || row.length < 4) continue;
      const [, runs] = row;
      out.push({ wkt: out.length + 1, runs: runs - prev, ended_at: row[2], ended_by: row[3] });
      prev = runs;
    }
    if (agg && (agg.runs ?? 0) > prev) {
      out.push({ wkt: "*", runs: (agg.runs ?? 0) - prev, current: true });
    }
    return out;
  })();
  // Bowler totals
  const bowlTot = (inn.bowling_card || []).reduce(
    (acc, b) => ({
      balls: acc.balls + (b.balls || 0),
      maidens: acc.maidens + (b.maidens || 0),
      runs_conceded: acc.runs_conceded + (b.runs_conceded || 0),
      wickets: acc.wickets + (b.wickets || 0),
    }),
    { balls: 0, maidens: 0, runs_conceded: 0, wickets: 0 }
  );
  const totalOvers = formatBowlerBalls(matchType, bowlTot.balls);
  const inningsLabel =
    inn.innings_number === 1 ? "1st" :
    inn.innings_number === 2 ? "2nd" :
    inn.innings_number === 3 ? "3rd" :
    `${inn.innings_number}th`;
  return (
    <div>
      {/* Innings header */}
      <div className="flex items-baseline justify-between mb-2 px-1">
        <h4 className="text-sm font-semibold text-text-primary">
          {inningsLabel} Innings — {inn.batting_team_name}
        </h4>
        {agg && (
          <span className="font-mono">
            <span className="font-bold text-base text-text-primary">{agg.runs ?? 0}</span>
            <span className="text-text-secondary">/</span>
            <span className="font-bold text-base text-text-primary">{agg.wickets ?? 0}</span>
            <span className="text-text-secondary text-xs ml-1">
              ({formatInningsProgress(matchType, { legalBalls: agg.legal_balls ?? 0, completedOvers: agg.completed_overs ?? 0 })})
            </span>
            {agg.is_chasing && (agg.target_runs || agg.revised_target_runs) && (() => {
              // Display as "runs to win" per cricket convention. DB stores
              // `target_runs` as opponent's raw total; the score-to-beat is
              // total+1. DLS values are stored as the exact score-to-reach
              // so no +1 in that branch.
              const isDLS = agg.revised_target_runs != null;
              const targetToWin = isDLS
                ? agg.revised_target_runs
                : agg.target_runs + 1;
              return (
                <span className="ml-3 text-[11px] uppercase tracking-wider text-amber-300">
                  Target {targetToWin}
                  {isDLS && <span className="ml-1 text-amber-200/80">DLS</span>}
                </span>
              );
            })()}
          </span>
        )}
      </div>

      {/* Batting card */}
      <div className="overflow-x-auto rounded-lg border border-border-default mb-3">
        <table className="w-full text-sm">
          <thead className="bg-bg-secondary/40 text-text-secondary text-[10px] uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">Batter</th>
              <th className="px-3 py-2 text-right">R</th>
              <th className="px-3 py-2 text-right">B</th>
              <th className="px-3 py-2 text-right">4s</th>
              <th className="px-3 py-2 text-right">6s</th>
              <th className="px-3 py-2 text-right">SR</th>
              <th className="px-3 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {(inn.batting_card || []).map((b) => (
              <tr key={b.player_id} className="border-t border-border-default">
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-text-primary">{b.player_name}</span>
                    {/* 2026-06-05 — "not out" pill only when there's no
                        dismissal_text either. Retired-hurt batters have
                        is_out=false but dismissal_text="retired hurt"
                        so they need the dismissal label, not N.O. */}
                    {(!b.is_out && !b.dismissal_text) && (
                      <span className="text-[9px] uppercase tracking-wider px-1 rounded border border-emerald-400/40 text-emerald-300">not out</span>
                    )}
                  </div>
                  {(b.is_out || b.dismissal_text) && (
                    <div className="text-[11px] text-rose-300/90 mt-0.5">
                      {b.dismissal_text || "out"}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono text-text-primary font-semibold">{b.runs}</td>
                {/* 2026-05-30 — batter "balls faced" shown as plain count
                    (reverted from the 2026-05-29 overs format). Cricket
                    convention: batter balls = legal deliveries faced; an
                    overs notation here would imply something the batter
                    was personally "on the field for" which isn't what
                    operators expect. Bowler card retains the X.Y format
                    via the b.overs field. */}
                <td className="px-3 py-2 text-right font-mono text-text-secondary">{b.balls}</td>
                <td className="px-3 py-2 text-right font-mono text-text-secondary">{b.fours}</td>
                <td className="px-3 py-2 text-right font-mono text-text-secondary">{b.sixes}</td>
                <td className="px-3 py-2 text-right font-mono text-text-secondary">{b.strike_rate ?? "—"}</td>
                <td className="px-3 py-2 text-right">
                  {(!b.is_out && !b.dismissal_text) && <span className="text-[9px] uppercase tracking-wider text-emerald-400 border border-emerald-400/40 rounded-full px-1.5 py-0.5">N.O.</span>}
                </td>
              </tr>
            ))}
            {(!inn.batting_card || inn.batting_card.length === 0) && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-text-secondary text-xs">No batting card yet.</td></tr>
            )}
          </tbody>
          <tfoot>
            {agg && (
              <tr className="border-t border-border-default bg-bg-secondary/30">
                <td className="px-3 py-2 text-text-secondary text-xs" colSpan={7}>
                  Extras: <span className="text-text-primary font-semibold">{agg.extras ?? 0}</span>
                  {" · "}Boundaries: <span className="text-text-primary font-semibold">{agg.boundary_count ?? 0}</span>
                  {" · "}Total: <span className="text-text-primary font-bold">
                    {agg.runs}
                    {agg.is_all_out ? (
                      <span className="ml-1 text-xs text-rose-300">all out</span>
                    ) : (
                      <>/{agg.wickets}</>
                    )}
                  </span>
                  {" "}in {formatInningsProgress(matchType, { legalBalls: agg.legal_balls ?? 0, completedOvers: agg.completed_overs ?? 0 })}{isHundred(matchType) ? "" : " ov"}
                </td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>

      {/* DNB */}
      {dnb.length > 0 && (
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-wider text-text-secondary mb-1">Did not bat</div>
          <div className="flex flex-wrap gap-1.5">
            {dnb.map((p) => (
              <span key={p.player_id} className="px-2 py-0.5 text-xs rounded bg-bg-secondary border border-border-default text-text-primary">
                {p.player_name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Fall of wickets.
          2026-06-10 — handle the 5th element `is_retired_hurt` that
          the backend emits for RETIRED_HURT / RETIRED_NOT_OUT entries
          (wkt=null in those rows). Without this branch, retired-hurt
          entries rendered as "-{runs}" because {null} prints empty
          alongside the literal "-" separator. Now they render with
          a "*" prefix + amber styling, matching the post-match
          Scorecard.jsx convention. */}
      {fow.length > 0 && (
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-wider text-text-secondary mb-1">Fall of wickets</div>
          <div className="flex flex-wrap gap-1.5 text-xs">
            {fow.map((row, idx) => {
              if (!Array.isArray(row) || row.length < 4) return null;
              const [wkt, runs, over, batter, isRetiredHurt] = row;
              if (isRetiredHurt) {
                return (
                  <span
                    key={idx}
                    className="px-2 py-0.5 rounded bg-amber-500/10 border border-amber-400/30 text-amber-200"
                    title="Retired hurt — not a wicket; the running total when the batter left the field"
                  >
                    <span className="font-mono text-amber-300">* {runs}</span>
                    <span className="opacity-60"> · </span>
                    <span>{batter}</span>
                    <span className="opacity-60"> · </span>
                    <span className="font-mono">{over} ov</span>
                    <span className="ml-1 text-[9px] uppercase tracking-wider opacity-70">retired</span>
                  </span>
                );
              }
              return (
                <span key={idx} className="px-2 py-0.5 rounded bg-bg-secondary border border-border-default text-text-secondary">
                  <span className="font-mono text-text-primary">{wkt}-{runs}</span>
                  <span className="opacity-60"> · </span>
                  <span>{batter}</span>
                  <span className="opacity-60"> · </span>
                  <span className="font-mono">{over} ov</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Partnerships — prefers server-computed (rich breakdown), falls back to FOW-derived */}
      {partnerships.length > 0 && (
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-wider text-text-secondary mb-1">Partnerships</div>
          <div className="overflow-x-auto rounded-lg border border-border-default">
            <table className="w-full text-sm">
              <thead className="bg-bg-secondary/40 text-text-secondary text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">For</th>
                  <th className="px-3 py-2 text-right">Runs (balls)</th>
                  <th className="px-3 py-2 text-left">Batters</th>
                  <th className="px-3 py-2 text-left">Ended at</th>
                  <th className="px-3 py-2 text-left">Ended by</th>
                </tr>
              </thead>
              <tbody>
                {partnerships.map((p, i) => (
                  <tr key={i} className="border-t border-border-default align-top">
                    <td className="px-3 py-2 font-mono text-text-primary">
                      {p.wkt === "*" ? "Unbroken" : `${p.wkt}${p.wkt === 1 ? "st" : p.wkt === 2 ? "nd" : p.wkt === 3 ? "rd" : "th"} wkt`}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-text-primary font-semibold">
                      {p.runs}
                      {p.balls != null && (
                        <span className="text-text-secondary font-normal ml-1">({p.balls})</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-text-secondary">
                      {Array.isArray(p.batters) && p.batters.length > 0 ? (
                        <div className="space-y-0.5">
                          {p.batters.map((b) => (
                            <div key={b.player_id} className="font-mono text-xs">
                              <span className="text-text-primary">{b.name}</span>
                              <span className="ml-1">{b.runs}({b.balls})</span>
                            </div>
                          ))}
                          {p.extras > 0 && (
                            <div className="font-mono text-[10px] text-text-secondary">extras {p.extras}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-text-secondary">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-text-secondary">{p.current ? "—" : `${p.ended_at} ov`}</td>
                    <td className="px-3 py-2 text-text-secondary">
                      {p.current ? <span className="text-emerald-400 text-[10px] uppercase tracking-wider">live</span> : p.ended_by}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bowling card */}
      <div className="overflow-x-auto rounded-lg border border-border-default">
        <table className="w-full text-sm">
          <thead className="bg-bg-secondary/40 text-text-secondary text-[10px] uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">Bowler ({inn.bowling_team_name})</th>
              <th className="px-3 py-2 text-right">{bowlerBallsColumnLabel(matchType)}</th>
              <th className="px-3 py-2 text-right">M</th>
              <th className="px-3 py-2 text-right">R</th>
              <th className="px-3 py-2 text-right">W</th>
              <th className="px-3 py-2 text-right">Econ</th>
            </tr>
          </thead>
          <tbody>
            {(inn.bowling_card || []).map((b) => (
              <tr key={b.player_id} className="border-t border-border-default">
                <td className="px-3 py-2">{b.player_name}</td>
                <td className="px-3 py-2 text-right font-mono text-text-secondary">{b.balls != null ? formatBowlerBalls(matchType, b.balls) : (b.overs ?? "—")}</td>
                <td className="px-3 py-2 text-right font-mono text-text-secondary">{b.maidens ?? 0}</td>
                <td className="px-3 py-2 text-right font-mono">{b.runs_conceded}</td>
                <td className="px-3 py-2 text-right font-mono text-text-primary font-semibold">{b.wickets}</td>
                <td className="px-3 py-2 text-right font-mono text-text-secondary">{b.economy ?? "—"}</td>
              </tr>
            ))}
            {(!inn.bowling_card || inn.bowling_card.length === 0) && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-text-secondary text-xs">No bowling card yet.</td></tr>
            )}
          </tbody>
          {(inn.bowling_card || []).length > 0 && (
            <tfoot>
              <tr className="border-t border-border-default bg-bg-secondary/30 font-mono text-text-secondary text-xs">
                <td className="px-3 py-2 text-right uppercase tracking-wider text-[10px]">Total</td>
                <td className="px-3 py-2 text-right">{totalOvers}</td>
                <td className="px-3 py-2 text-right">{bowlTot.maidens}</td>
                <td className="px-3 py-2 text-right">{bowlTot.runs_conceded}</td>
                <td className="px-3 py-2 text-right text-text-primary font-semibold">{bowlTot.wickets}</td>
                <td className="px-3 py-2"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
